import {
  type AccountInfo,
  InteractionRequiredAuthError,
  PublicClientApplication,
} from '@azure/msal-browser';
import { type Account, type AuthBackend, apiScopes, authority, isRealEntra } from './auth.common';
import { config } from './config';

function toAccount(a: AccountInfo): Account {
  const claims = (a.idTokenClaims ?? {}) as Record<string, unknown>;
  return {
    // `localAccountId` IS the oid for a single-tenant token, but only when the claim is
    // absent do we fall back — the claim is the contract, the fallback is the guess.
    oid: (claims.oid as string | undefined) ?? a.localAccountId,
    upn: a.username,
    name: a.name ?? a.username,
  };
}

/**
 * Web sign-in: MSAL, auth-code + PKCE, `loginRedirect` (18.10).
 *
 * Redirect rather than popup, matching the review console: it needs no `frame-src` for the
 * login host and survives a popup blocker on a locked-down machine. The phone's web build
 * is the same bundle, and a popup on a handset is worse still.
 *
 * `knownAuthorities` is the one place this differs from the review console's copy, and
 * only for the end-to-end run: MSAL will not send anyone to an authority it has not been
 * told to trust, and the run's issuer is not a Microsoft host. MSAL stays in its normal
 * AAD mode either way — it appends `/v2.0` to the authority for discovery, which is what
 * real Entra serves and what the mock is pointed at, so the run exercises the same code
 * path a release does. That branch is dead in a release: `ENTRA_ISSUER` is '' there.
 *
 * MSAL also refuses a plain-http authority outright (`authority_uri_insecure`, no
 * localhost exemption — checked, not assumed), which is why the run's issuer serves TLS.
 */
function build(): AuthBackend {
  const real = isRealEntra();
  const pca = new PublicClientApplication({
    auth: {
      clientId: config.entraClientId,
      authority: authority(),
      redirectUri: config.entraRedirectUri || window.location.origin + window.location.pathname,
      postLogoutRedirectUri: window.location.origin,
      ...(real ? {} : { knownAuthorities: [new URL(authority()).host] }),
    },
    /*
      sessionStorage, as the review console uses. A shared field device should not keep a
      signed-in operator across browser sessions: the next person to pick it up would
      submit under the previous operator's oid, and `operator_oid` is the audit trail.
    */
    cache: { cacheLocation: 'sessionStorage' },
  });

  const scopes = apiScopes();
  let ready: Promise<void> | null = null;
  const ensure = () => {
    ready ??= pca.initialize();
    return ready;
  };

  const active = (): AccountInfo | null => {
    const a = pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null;
    if (a && !pca.getActiveAccount()) pca.setActiveAccount(a);
    return a;
  };

  return {
    async init() {
      await ensure();
      const result = await pca.handleRedirectPromise();
      if (result?.account) pca.setActiveAccount(result.account);
      const a = active();
      return a ? toAccount(a) : null;
    },

    async signIn() {
      await ensure();
      /*
        Resolves when the redirect is UNDER WAY, not when it has completed — the page is
        about to unload, and the account is reported by `init()` on the way back in. A
        caller that awaits this expecting an account gets null, which is why the return
        type says so rather than pretending.
      */
      await pca.loginRedirect({ scopes });
      return null;
    },

    async signOut() {
      await ensure();
      const account = active() ?? undefined;
      await pca.logoutRedirect({ account });
    },

    async getAccessToken(force = false) {
      await ensure();
      const account = active();
      if (!account) return null;
      try {
        const r = await pca.acquireTokenSilent({ scopes, account, forceRefresh: force });
        return r.accessToken;
      } catch (e) {
        if (e instanceof InteractionRequiredAuthError) {
          await pca.acquireTokenRedirect({ scopes, account });
          return null;
        }
        throw e;
      }
    },
  };
}

/*
  ONE backend per app, not one per caller.

  `api.ts` and `useAuth` both ask for it, and building two meant two MSAL instances with
  two token caches: sign-in populated one and every request read the other, so the app
  signed in successfully and then sent no Authorization header at all. The 401 said
  "provide x-api-key or an Entra Bearer token", which is exactly right and says nothing
  about there being two of anything.
*/
let instance: AuthBackend | null = null;

export function createAuth(): AuthBackend {
  instance ??= build();
  return instance;
}
